import React, { useState } from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';
import { useWorkflowStore } from '../../store/workflowStore';
import { useAssistantChatStore } from '../../store/assistantChatStore';
import ClearSessionConfirmDialog from './ClearSessionConfirmDialog';
import Tooltip from '../common/Tooltip';

interface ClearSessionButtonProps {
  workflowId: string;
  isDisabled?: boolean;
  className?: string;
}

const ClearSessionButton: React.FC<ClearSessionButtonProps> = ({
  workflowId,
  isDisabled = false,
  className = '',
}) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  const execution = useWorkflowStore((state) =>
    state.executions.get(workflowId)
  );
  const clearSessionData = useWorkflowStore((state) => state.clearSessionData);

  // Also check assistantChatStore for session data (important for stopped orchestrations)
  const assistantMessages = useAssistantChatStore((state) => state.messagesBySession);
  const assistantMetadata = useAssistantChatStore((state) => state.sessionMetadata);

  // Count messages from workflow store
  const orchestrationMessageCount = execution?.sessionMessages?.length || 0;
  const sessionCount = Object.keys(execution?.sessionInfoMap || {}).length;

  // Count messages from assistant chat store for this workflow
  const assistantSessionKeys = Object.keys(assistantMessages).filter(key => {
    const metadata = assistantMetadata[key];
    return metadata?.workflowId === parseInt(workflowId);
  });
  const assistantMessageCount = assistantSessionKeys.reduce((total, key) => {
    return total + (assistantMessages[key]?.length || 0);
  }, 0);

  // Total message count from both stores
  const messageCount = Math.max(orchestrationMessageCount, assistantMessageCount);

  const isRunning = execution?.isRunning || false;
  const hasSessionData = messageCount > 0 || sessionCount > 0 || assistantSessionKeys.length > 0;

  const handleClick = () => {
    if (!hasSessionData) return;
    setShowConfirmDialog(true);
  };

  const handleConfirm = async (stopWorkflow: boolean) => {
    setIsClearing(true);
    try {
      await clearSessionData(workflowId, {
        stopWorkflow,
        clearMode: 'soft',
        reason: 'User requested session clear from UI'
      });
      setShowConfirmDialog(false);
    } catch (error) {
      console.error('Failed to clear session data:', error);
      // Error is already handled in the store, just close the dialog
      setShowConfirmDialog(false);
    } finally {
      setIsClearing(false);
    }
  };

  const handleCancel = () => {
    setShowConfirmDialog(false);
  };

  return (
    <>
      <Tooltip content={!hasSessionData ? 'No session data to clear' : 'Clear session data'}>
      <button
        onClick={handleClick}
        disabled={isDisabled || !hasSessionData || isClearing}
        className={`
          flex items-center space-x-2 px-4 py-2 text-sm font-medium
          border rounded-md
          ${hasSessionData && !isDisabled && !isClearing
            ? 'text-red-700 dark:text-red-400 bg-white dark:bg-red-900/20 border-red-300 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/30 hover:border-red-400 dark:hover:border-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2'
            : 'text-gray-400 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 cursor-not-allowed'
          }
          transition-colors
          ${className}
        `}

      >
        {isClearing ? (
          <>
            <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
            <span>Clearing...</span>
          </>
        ) : (
          <>
            <TrashIcon className="w-4 h-4" />
            <span>Clear Session</span>
          </>
        )}
      </button>
      </Tooltip>

      <ClearSessionConfirmDialog
        isOpen={showConfirmDialog}
        messageCount={messageCount}
        sessionCount={sessionCount}
        isRunning={isRunning}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
};

export default ClearSessionButton;
